import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/contexts/workspace-context';
import { useDisconnectChannel, useRefreshChannel } from '@/components/channels/queries';
import type { WhatsAppChannel } from '@/types/channel';
import { emit } from '@/lib/analytics';

interface DisconnectCoexistenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Coexistence disconnect is WhatsApp-only; callers must guard with
  // channel.type === 'whatsapp' before passing.
  channel: WhatsAppChannel;
  onDisconnected: () => void;
}

export function DisconnectCoexistenceDialog({
  open,
  onOpenChange,
  channel,
  onDisconnected,
}: DisconnectCoexistenceDialogProps) {
  const { activeWorkspace } = useWorkspace();
  const refresh = useRefreshChannel(activeWorkspace?.id);
  const disconnect = useDisconnectChannel();
  const refreshing = refresh.isPending || disconnect.isPending;
  const [error, setError] = useState<string | null>(null);

  // Refresh only re-probes Meta and re-badges; it never flips channelStatus.
  // Once Meta reports the WABA unsubscribed, run the real lifecycle
  // disconnect so the row stops owning the number (AIT-575). Before that,
  // the probe would immediately re-badge a still-subscribed channel.
  function handleRefresh() {
    setError(null);
    refresh.mutate(
      { channelId: channel.id },
      {
        onSuccess: (data) => {
          if (data.metaConnected !== false) {
            setError(
              'Still connected -- please follow the instructions above and try again.',
            );
            return;
          }
          disconnect.mutate(
            { channelId: channel.id },
            {
              onSuccess: () => {
                emit('channel_disconnected', { channel_id: channel.id, waba_name: channel.whatsappWabaName, connection_type: channel.connectionType });
                onOpenChange(false);
                onDisconnected();
              },
              onError: () =>
                setError('Could not disconnect channel. Check your connection and try again.'),
            },
          );
        },
        onError: () => setError('Could not check channel status. Please try again.'),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!refreshing) {
          onOpenChange(nextOpen);
          if (!nextOpen) setError(null);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Disconnect{' '}
            {channel.whatsappWabaName || 'this channel'}?
          </DialogTitle>
          <DialogDescription>
            This channel uses Coexistence mode. You need to disconnect it from
            Meta&apos;s side first:
          </DialogDescription>
        </DialogHeader>

        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Go to Meta Business Suite</li>
          <li>Navigate to Settings &gt; WhatsApp Accounts</li>
          <li>Remove HookMyApp from your WABA</li>
        </ol>

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            window.open('https://business.facebook.com', '_blank', 'noopener,noreferrer')
          }
        >
          Open Meta Business Suite
          <ExternalLink className="size-3" />
        </Button>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            Keep Channel
          </DialogClose>
          <Button
            disabled={refreshing}
            onClick={handleRefresh}
          >
            {refreshing && <Loader2 className="size-3.5 animate-spin" />}
            {refreshing ? 'Checking...' : 'Refresh'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
